import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Plus, ChevronLeft, ChevronRight, FileText, Clock, Loader2, Download } from 'lucide-react'
import { Card, CardContent, Button } from '@/components/ui'
import { useFamily } from '@/context/FamilyContext'
import { useStudio } from '@/context/StudioContext'
import { useWritingStudio } from '@/context/WritingStudioContext'
import { useToast } from '@/context/ToastContext'
import { UpgradeBanner } from '@/components/UpgradeBanner'
import type { ProjectRecord } from '@/types/writingStudio'

export function ProjectList() {
  const navigate = useNavigate()
  const { activeWriter } = useFamily()
  const { mode } = useStudio()
  const { repository, readOnly } = useWritingStudio()
  const { showToast } = useToast()

  const [projects, setProjects] = useState<ProjectRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isCreating, setIsCreating] = useState(false)
  const [canCreateProject, setCanCreateProject] = useState(false)

  // Load projects on mount
  useEffect(() => {
    async function loadProjects() {
      if (!activeWriter) return

      try {
        const result = await repository.listProjects(activeWriter.id)
        if (result.success) {
          setProjects(result.data)
        } else {
          showToast(result.error || 'Could not load projects', 'error')
        }

        // Check if can create new project
        const canCreate = await repository.canCreateProject(activeWriter.id)
        setCanCreateProject(canCreate)
      } catch (err) {
        console.error('Failed to load projects:', err)
        showToast('Could not load projects', 'error')
      } finally {
        setIsLoading(false)
      }
    }

    loadProjects()
  }, [activeWriter, repository, showToast])

  const handleCreateProject = async () => {
    if (!activeWriter || readOnly) {
      if (readOnly) {
        showToast('Your subscription has lapsed. Renew to create projects.', 'error')
      }
      return
    }

    setIsCreating(true)
    try {
      const result = await repository.createProject(activeWriter.id)
      if (result.success) {
        // Navigate to the new project
        navigate(`/studio/write/${result.data.project.id}`)
      } else {
        showToast(result.error || 'Could not create project', 'error')
      }
    } catch (err) {
      console.error('Failed to create project:', err)
      showToast('Could not create project', 'error')
    } finally {
      setIsCreating(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })
  }

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--color-kids-primary)]" />
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Upgrade banner for local mode */}
      {mode === 'local' && (
        <div className="mb-6">
          <UpgradeBanner />
        </div>
      )}

      {/* Back link */}
      <Link
        to="/studio"
        className="inline-flex items-center gap-2 text-[var(--color-kids-text-muted)] hover:text-[var(--color-kids-text)] transition-colors mb-6"
      >
        <ChevronLeft className="w-5 h-5" />
        <span className="font-medium">Writing Studio</span>
      </Link>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-8"
      >
        <div>
          <h1 className="font-display text-[var(--font-size-kid-2xl)] font-bold text-[var(--color-kids-text)] mb-1">
            My Projects
          </h1>
          <p className="text-[var(--color-kids-text-muted)]">
            {activeWriter?.display_name}'s writing projects
          </p>
        </div>

        {canCreateProject && (
          <Button onClick={handleCreateProject} disabled={isCreating || readOnly}>
            {isCreating ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <Plus className="w-5 h-5 mr-2" />
            )}
            New Project
          </Button>
        )}
      </motion.div>

      {/* Projects list */}
      {projects.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="border-dashed border-2 border-[var(--color-kids-border)]">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 mx-auto rounded-full bg-[var(--color-kids-primary)]/10 flex items-center justify-center mb-6">
                <FileText className="w-10 h-10 text-[var(--color-kids-primary)]" />
              </div>
              <h2 className="font-display text-xl font-bold text-[var(--color-kids-text)] mb-3">
                Start Your First Project
              </h2>
              <p className="text-[var(--color-kids-text-muted)] mb-6 max-w-md mx-auto">
                Create a new writing project to start your story. You can organize it into chapters,
                add notes, and export when you're ready to share!
              </p>
              <Button size="lg" onClick={handleCreateProject} disabled={isCreating || readOnly}>
                {isCreating ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-5 h-5 mr-2" />
                )}
                Create My First Project
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      ) : (
        <div className="space-y-4">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Link to={`/studio/write/${project.id}`}>
                <Card className="hover:shadow-lg transition-all hover:scale-[1.01] cursor-pointer group">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      {/* Cover emoji */}
                      <div className="w-14 h-14 rounded-[var(--radius-kid)] bg-[var(--color-kids-surface-alt)] flex items-center justify-center text-3xl flex-shrink-0">
                        {project.coverEmoji}
                      </div>

                      {/* Project info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-display font-bold text-lg text-[var(--color-kids-text)] truncate group-hover:text-[var(--color-kids-primary)] transition-colors">
                          {project.title}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-[var(--color-kids-text-muted)]">
                          <span>{project.wordCount.toLocaleString()} words</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {formatDate(project.updatedAt)}
                          </span>
                        </div>
                      </div>

                      {/* Arrow */}
                      <ChevronRight className="w-5 h-5 text-[var(--color-kids-text-muted)] group-hover:text-[var(--color-kids-primary)] group-hover:translate-x-1 transition-all flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}

          {/* Limit reached message */}
          {!canCreateProject && projects.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="border-[var(--color-kids-warning)]/30 bg-[var(--color-kids-warning)]/5">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[var(--color-kids-text)]">
                        Ready to start a new project?
                      </p>
                      <p className="text-xs text-[var(--color-kids-text-muted)] mt-1">
                        Free accounts can have 1 project. Export your current project first to keep a backup.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Link to={`/studio/write/${projects[0].id}`}>
                        <Button variant="ghost" size="sm">
                          <Download className="w-4 h-4 mr-1" />
                          Export
                        </Button>
                      </Link>
                      <Link to="/upgrade">
                        <Button variant="secondary" size="sm">
                          Upgrade
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>
      )}

      {/* Info footer */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-center text-sm text-[var(--color-kids-text-muted)] mt-8"
      >
        Projects are saved to this device. Export to keep a copy!
      </motion.p>
    </div>
  )
}
